import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Tooltip
} from '@chakra-ui/react';
import {
  faCirclePause,
  faCirclePlay,
  faLink,
  faPencil,
  faTrash
} from '@fortawesome/pro-regular-svg-icons';
import { ButtonIcon, Modal, useToast } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  exportConfigItem,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { ISubmissionContextJournal } from '@publishing-engine/domain/views';
import { useState } from 'react';
import CreateConfiguration from './CreateConfiguration';
import { useWorkspace } from 'src/pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import {
  useDeleteExportConfig,
  useToggleExportConfig
} from 'src/api/mutations';
import { testConnections } from './utils/VendorExportUtilities';
import { formatDateAndTime } from '../../utils/date';
interface VendorExportConfigTableAdminProps {
  submissionContext: ISubmissionContext | ISubmissionContextJournal;
}
const VendorExportConfigTableAdmin = ({
  submissionContext
}: VendorExportConfigTableAdminProps) => {
  const [editingConfig, setEditingConfig] = useState<exportConfigItem | null>(
    null
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const { orgId } = useWorkspace();
  const { toggleExportConfigurationAsync } = useToggleExportConfig(
    submissionContext?.id,
    orgId
  );
  const toast = useToast();

  const { deleteExportConfigurationAsync } = useDeleteExportConfig(
    submissionContext?.id,
    orgId
  );
  return (
    <>
      {submissionContext?.config.exportConfig &&
      submissionContext?.config.exportConfig.length > 0 ? (
        <>
          <Box overflowX="auto" width="100%">
            <Table
              variant="simple"
              bg="white"
              sx={{
                '& th, & td': {
                  px: 4,
                  py: 3
                }
              }}
            >
              <Thead>
                <Tr
                  data-testid="exports-table-header"
                  sx={{
                    '& th': {
                      textTransform: 'none',
                      color: 'black',
                      background: 'white'
                    }
                  }}
                >
                  <Th>
                    <Text textStyle="headingItem" whiteSpace="nowrap">
                      Export Name
                    </Text>
                  </Th>
                  <Th>
                    <Text textStyle="headingItem" whiteSpace="nowrap">
                      Configure Type
                    </Text>
                  </Th>
                  <Th>
                    <Text textStyle="headingItem" whiteSpace="nowrap">
                      Created
                    </Text>
                  </Th>
                  <Th>
                    <Text textStyle="headingItem" whiteSpace="nowrap">
                      Updated
                    </Text>
                  </Th>
                  <Th textAlign="left" />
                </Tr>
              </Thead>
              <Tbody>
                {submissionContext?.config.exportConfig?.map((item, index) => (
                  <Tr key={index} data-testid={`exports-table-row-${index}`}>
                    <Td>
                      <Flex direction="column">
                        <Text fontWeight="semibold">{item.name}</Text>
                        <Text fontSize="sm">
                          {item.exportType === 'full'
                            ? 'Package file'
                            : 'Metadata Only'}
                        </Text>
                      </Flex>
                    </Td>
                    <Td>
                      <Text>{item.exportConfigType.toUpperCase()}</Text>
                    </Td>
                    <Td>
                      <Text>{item.createdBy}</Text>
                      <Text fontSize="sm">
                        {item.createdAt !== '-' &&
                          formatDateAndTime(item.createdAt)}
                      </Text>
                    </Td>
                    <Td>
                      <Text>{item.updatedBy}</Text>
                      <Text fontSize="sm">
                        {item.updatedAt !== '-' &&
                          formatDateAndTime(item.updatedAt)}
                      </Text>
                    </Td>
                    <Td p={0}>
                      <Flex gap={5}>
                        <Flex gap={2} justifyContent={'center'}>
                          <Tooltip
                            label={`${
                              item?.isActive ? 'Pause' : 'Resume'
                            } export configuration`}
                            hasArrow
                            shouldWrapChildren
                            placement="top"
                          >
                            <ButtonIcon
                              size="small"
                              ariaLabel="Edit Export and Retry"
                              icon={
                                <FontAwesomeIcon
                                  icon={
                                    item?.isActive
                                      ? faCirclePause
                                      : faCirclePlay
                                  }
                                />
                              }
                              data-cy="contributions-info-icon"
                              onClick={async () => {
                                await toggleExportConfigurationAsync({
                                  ...item,
                                  isActive: item?.isActive ? false : true
                                });
                              }}
                              disabled={item.exportConfigType === 'manual'}
                            />
                          </Tooltip>
                        </Flex>

                        <Flex gap={2} justifyContent={'center'}>
                          <Tooltip
                            label="Edit export configuration"
                            hasArrow
                            shouldWrapChildren
                            placement="top"
                          >
                            <ButtonIcon
                              size="small"
                              ariaLabel="Edit Export and Retry"
                              icon={<FontAwesomeIcon icon={faPencil} />}
                              data-cy="contributions-info-icon"
                              onClick={() => {
                                setEditingConfig(item);
                                setIsEditModalOpen(true);
                              }}
                              disabled={item.exportConfigType === 'manual'}
                            />
                          </Tooltip>
                        </Flex>
                        <Flex gap={2} justifyContent={'center'}>
                          <Tooltip
                            label={'Test Connection'}
                            hasArrow
                            shouldWrapChildren
                            placement="top"
                          >
                            <ButtonIcon
                              size="small"
                              ariaLabel="Test Connection"
                              icon={<FontAwesomeIcon icon={faLink} />}
                              data-cy="contributions-info-icon"
                              onClick={async () => {
                                await testConnections(item, toast);
                              }}
                              disabled={item.exportConfigType === 'manual'}
                            />
                          </Tooltip>
                        </Flex>
                        <Flex gap={2} justifyContent={'center'}>
                          <Tooltip
                            label={'Delete Configuration'}
                            hasArrow
                            shouldWrapChildren
                            placement="top"
                          >
                            <ButtonIcon
                              size="small"
                              ariaLabel="Delete Configuration"
                              icon={<FontAwesomeIcon icon={faTrash} />}
                              data-cy="contributions-info-icon"
                              disabled={item.exportConfigType === 'manual'}
                              onClick={async () => {
                                try {
                                  await deleteExportConfigurationAsync(item.id);
                                  toast({
                                    variant: 'success',
                                    title:
                                      'Export configuration deleted successfully'
                                  });
                                } catch (error) {
                                  toast({
                                    variant: 'error',
                                    title:
                                      'Failed to delete export configuration'
                                  });
                                }
                              }}
                            />
                          </Tooltip>
                        </Flex>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          {editingConfig && (
            <Modal
              isOpen={isEditModalOpen}
              onClose={() => {
                setEditingConfig(null);
                setIsEditModalOpen(false);
              }}
              width={{ base: 'calc(100vw - 16px)', md: '720px' } as any}
              closeButtonProps={{
                hidden: false
              }}
            >
              <CreateConfiguration
                submissionContextId={submissionContext?.id}
                setIsOpen={setIsEditModalOpen}
                orgId={orgId}
                isEdit={true}
                config={editingConfig}
              />
            </Modal>
          )}
        </>
      ) : (
        <NoRecordFound
          title="No export configurations"
          subTitle="No export configurations found for this submission"
        />
      )}
    </>
  );
};

export default VendorExportConfigTableAdmin;
